import { useCallback, useMemo } from "react";
import { Circle, Group, Image, Layer, Line, Rect, Stage } from "react-konva";
import useImage from "use-image";
import useWindowDimensions from "../../components/formatted/UseWindowsDimensions";
import {
  buildMaskCoordinateContext,
  originalToStage,
  polygonToStageLinePoints,
  stageToOriginal,
} from "./installationMaskCoordinates";
import {
  getMaskImagePreviewGroupProps,
  getMaskPreviewStageDimensions,
} from "./installationRotationUtils";

function resolveEffectiveDimensions(dimensions, image) {
  const naturalWidth = image?.naturalWidth || image?.width;
  const naturalHeight = image?.naturalHeight || image?.height;

  if (naturalWidth > 0 && naturalHeight > 0) {
    return {
      processedWidth: naturalWidth,
      processedHeight: naturalHeight,
      originalWidth: Math.max(naturalWidth, naturalHeight),
      originalHeight: Math.min(naturalWidth, naturalHeight),
    };
  }

  return dimensions;
}

const InstallationMaskEditor = ({
  imageUrl,
  dimensions,
  rotationAngle,
  imagePreviewRotation = 0,
  draftPoints,
  onDraftPointsChange,
  disabled,
}) => {
  const { widthWindow, heightWindow } = useWindowDimensions();
  const [image] = useImage(imageUrl, "anonymous");

  const effectiveDimensions = useMemo(
    () => resolveEffectiveDimensions(dimensions, image),
    [dimensions, image],
  );

  const layout = useMemo(() => {
    const sourceWidth = Math.max(effectiveDimensions.processedWidth, 1);
    const sourceHeight = Math.max(effectiveDimensions.processedHeight, 1);
    const maxWidth = Math.min(Math.max(widthWindow - 32, 260), 520);
    const maxHeight = Math.min(Math.max(heightWindow - 280, 220), 520);
    const displayDimensions = getMaskPreviewStageDimensions(
      sourceWidth,
      sourceHeight,
      imagePreviewRotation,
    );
    const scale = Math.min(
      maxWidth / displayDimensions.width,
      maxHeight / displayDimensions.height,
    );
    const imageWidth = sourceWidth * scale;
    const imageHeight = sourceHeight * scale;
    const stageDimensions = getMaskPreviewStageDimensions(
      imageWidth,
      imageHeight,
      imagePreviewRotation,
    );

    return {
      scale,
      imageWidth,
      imageHeight,
      stageWidth: stageDimensions.width,
      stageHeight: stageDimensions.height,
      imageGroup: getMaskImagePreviewGroupProps(
        imagePreviewRotation,
        imageWidth,
        imageHeight,
      ),
    };
  }, [
    widthWindow,
    heightWindow,
    effectiveDimensions.processedWidth,
    effectiveDimensions.processedHeight,
    imagePreviewRotation,
  ]);

  const coordinateContext = useMemo(
    () => buildMaskCoordinateContext(effectiveDimensions, rotationAngle),
    [effectiveDimensions, rotationAngle],
  );

  const linePoints = useMemo(
    () =>
      polygonToStageLinePoints(draftPoints, coordinateContext, layout.scale),
    [draftPoints, coordinateContext, layout.scale],
  );

  const addPointFromEvent = useCallback(
    (event) => {
      if (disabled) {
        return;
      }

      const target = event.target;
      if (target.getClassName() === "Circle") {
        return;
      }

      const stage = target.getStage();
      if (!stage) {
        return;
      }

      const pointer = stage.getPointerPosition();
      if (!pointer) {
        return;
      }

      const originalPoint = stageToOriginal(
        [pointer.x, pointer.y],
        coordinateContext,
        layout.scale,
      );

      onDraftPointsChange((currentPoints) => [...currentPoints, originalPoint]);
    },
    [disabled, coordinateContext, layout.scale, onDraftPointsChange],
  );

  const updatePoint = useCallback(
    (index, originalPoint) => {
      onDraftPointsChange((currentPoints) =>
        currentPoints.map((point, pointIndex) =>
          pointIndex === index ? originalPoint : point,
        ),
      );
    },
    [onDraftPointsChange],
  );

  if (!imageUrl) {
    return null;
  }

  return (
    <div className="installation-mask-editor">
      <Stage
        width={layout.stageWidth}
        height={layout.stageHeight}
        className="installation-mask-editor-stage"
      >
        <Layer>
          <Rect
            x={0}
            y={0}
            width={layout.stageWidth}
            height={layout.stageHeight}
            fill="rgba(0, 0, 0, 0.01)"
            onClick={addPointFromEvent}
            onTap={addPointFromEvent}
          />
          <Group
            rotation={layout.imageGroup.rotation}
            x={layout.imageGroup.x}
            y={layout.imageGroup.y}
            width={layout.imageGroup.width}
            height={layout.imageGroup.height}
          >
            {image && (
              <Image
                image={image}
                width={layout.imageWidth}
                height={layout.imageHeight}
                listening={false}
              />
            )}
          </Group>
          {draftPoints.length >= 2 && (
            <Line
              points={linePoints}
              stroke="#00a7a6"
              strokeWidth={2}
              fill="rgba(0, 167, 166, 0.28)"
              closed={draftPoints.length >= 3}
              listening={false}
            />
          )}
          {draftPoints.map((point, index) => {
            const [stageX, stageY] = originalToStage(
              point,
              coordinateContext,
              layout.scale,
            );
            return (
              <Circle
                key={`mask-point-${index}`}
                x={stageX}
                y={stageY}
                radius={12}
                fill="#00a7a6"
                stroke="#ffffff"
                strokeWidth={2}
                hitStrokeWidth={20}
                draggable={!disabled}
                onDragMove={(event) => {
                  const node = event.target;
                  updatePoint(
                    index,
                    stageToOriginal(
                      [node.x(), node.y()],
                      coordinateContext,
                      layout.scale,
                    ),
                  );
                }}
                onDragEnd={(event) => {
                  const node = event.target;
                  updatePoint(
                    index,
                    stageToOriginal(
                      [node.x(), node.y()],
                      coordinateContext,
                      layout.scale,
                    ),
                  );
                }}
              />
            );
          })}
        </Layer>
      </Stage>
    </div>
  );
};

export default InstallationMaskEditor;
